import X2JS from 'xml-json-parser';
import { toState } from 'src/services/converters/cylinderWarp';
import { toState as garmentMarkersToState } from 'src/services/converters/garmentMarkers';
import { toState as garmentPlacementsToState } from 'src/services/converters/garmentPlacement';
import { TEXTURE_TRANSFORM } from 'src/models/transforms';
import { GARMENT_WARP } from 'src/models/documentWarps';
import { xmlToBackgroundObject } from './backgrounds';
import { xmlToDocumentObjects } from './documents';
import { xmlToOverlayObjects } from './overlays';
import { xmlToTransformObjects } from './transforms';
import xmlToMetadataObject from './metadata';
import type { XMLTree_DIP } from 'src/types/xml';
import { ENGRAVING_CHANNEL, GLOW_FILTER } from 'src/config/constants';
import { FinishData } from 'src/services/materialClient';
import { Document } from 'src/pages/editor/components/documents/slice';
import { EffectType } from 'src/config/effects';
import { GarmentMarkersType } from 'src/types/decorationSpecs';
import { cloneDeep } from 'lodash';

export type TypeComposeComposite = {
    backgrounds: {
        selected: string;
        list: {
            id: string;
            mode: string;
            url: string;
            uploading: boolean;
            name: string;
            width: number;
            height: number;
            garmentMarkers?: GarmentMarkersType;
        }[];
    };
    overlays: {
        mode: string;
        url: string;
        name: string;
        uploading: boolean;
        id: string;
    }[];
    documents: {
        past: Document[] | Document[][];
        present: Document[];
        future: never[];
    };
    aspectRatio?: string;
};

const composeComposite = (
    dip: XMLTree_DIP,
    finishList: FinishData[],
    cylinderWarp?: Record<string, any>,
    markers?: Record<string, any>,
    garmentPlacements?: Record<string, any>,
) => {
    const backgrounds = xmlToBackgroundObject(dip.Composite);

    const garmentMarkers = markers ? garmentMarkersToState(markers.Markers) : [];
    (backgrounds.list as TypeComposeComposite['backgrounds']['list']).forEach((background) => {
        const matchingMarkers = garmentMarkers.find((marker) => marker.id === background.id);
        if (matchingMarkers) {
            const { id, ...garmentMarkersFields } = matchingMarkers;
            // eslint-disable-next-line no-param-reassign
            background.garmentMarkers = garmentMarkersFields;
        }
    });
    const overlays = xmlToOverlayObjects(dip.Composite);
    const { documents: initialDocuments, finishTransformNames } = xmlToDocumentObjects(dip, finishList);

    const docs = initialDocuments.map((i) => cloneDeep(i));

    const documents = {
        past: docs,
        present: initialDocuments,
        future: [],
    };

    const transformsById = xmlToTransformObjects(dip, finishTransformNames);
    const metadata = xmlToMetadataObject(dip.Metadata);

    const cylinderWarps = cylinderWarp ? toState(cylinderWarp.Cylinders) : [];
    // A GARMENT_WARP document's XML transform is an ordinary RectangleWarp — indistinguishable
    // from RECTANGLE_WARP's — so xmlToDocumentObjects always reads its warpType back as
    // RectangleWarp (see documents.ts's transformIdMap, which has no GarmentWarp case). This join,
    // mirroring garmentMarkers' join onto backgrounds above, is the only way to tell a document
    // was actually in GARMENT_WARP mode when saved and restore that instead of leaving it
    // downgraded to Rectangle.
    const documentGarmentPlacements = garmentPlacements
        ? garmentPlacementsToState(garmentPlacements.GarmentPlacements)
        : [];

    documents.present.forEach((doc) => {
        doc.transforms = transformsById[doc.id]; // eslint-disable-line no-param-reassign
        const matchingCylinder = cylinderWarps.find((cylinder) => cylinder.id === doc.id);
        if (matchingCylinder) {
            doc.cylinder = matchingCylinder; // eslint-disable-line no-param-reassign
        }

        const matchingGarmentPlacement = documentGarmentPlacements.find((placement) => placement.id === doc.id);
        if (matchingGarmentPlacement) {
            const { id, ...garmentPlacementFields } = matchingGarmentPlacement;
            doc.warpType = GARMENT_WARP; // eslint-disable-line no-param-reassign
            doc.garmentPlacement = garmentPlacementFields; // eslint-disable-line no-param-reassign
        }

        const texture = doc.transforms.find((transform) => transform.type === TEXTURE_TRANSFORM);
        if (texture) {
            doc.textureName = texture.id.substring(1); // eslint-disable-line no-param-reassign
        }

        // Check for GlowFilter in transforms and update effect properties
        const glowFilter = doc.transforms.find((transform: any) => transform.type === GLOW_FILTER) as any;
        if (glowFilter) {
            if (doc.effect?.type === EffectType.GLOW) {
                // Update the glow effect with actual values from GlowFilter (source of truth)
                doc.effect.r = glowFilter.r;
                doc.effect.g = glowFilter.g;
                doc.effect.b = glowFilter.b;
                doc.effect.radius = glowFilter.radius;
                doc.effect.light = glowFilter.light;
            } else if (!doc.effect) {
                // Fallback: create effect if not already set (shouldn't happen with new structure)
                doc.effect = {
                    type: EffectType.GLOW,
                    r: glowFilter.r,
                    g: glowFilter.g,
                    b: glowFilter.b,
                    radius: glowFilter.radius,
                    light: glowFilter.light,
                };
            }
        }

        // Check for LetterpressFilter in transforms and update effect properties
        const letterPressFilter = doc.transforms.find(
            (transform: any) => transform.type === 'LetterpressFilter',
        ) as any;
        if (letterPressFilter) {
            if (doc.effect?.type === EffectType.LETTERPRESS) {
                // Update depth from LetterpressFilter (source of truth)
                // The Document element's depth attribute is layer depth, not effect depth
                doc.effect.depth = letterPressFilter.depth;
            } else if (!doc.effect) {
                // Fallback: create effect if not already set (shouldn't happen with new structure)
                doc.effect = {
                    type: EffectType.LETTERPRESS,
                    mode: 'softlight', // Default mode
                    depth: letterPressFilter.depth,
                };
            }
        }

        // check for rotation on warp
        const rotation = metadata.find(
            (data) => data.type === 'rotation' && `_${data.data.transformId}` === doc.transforms[0].id,
        );
        const engraveColor = metadata.find((data) => data.type === ENGRAVING_CHANNEL && `_${data.data.id}` === doc.id);

        if (rotation) {
            doc.rotationAngle = rotation.data.rotationAngle; // eslint-disable-line no-param-reassign
        } else {
            doc.rotationAngle = 0; // eslint-disable-line no-param-reassign
        }

        if (engraveColor && doc.effect?.type === EffectType.ENGRAVING) {
            doc.effect.color = engraveColor.data.color; // eslint-disable-line no-param-reassign
        }
    });

    const stateValue: TypeComposeComposite = { backgrounds, overlays, documents };
    const aspectRatio = metadata.find((data) => data.type === 'aspectRatio');
    if (aspectRatio) {
        stateValue.aspectRatio = aspectRatio.data;
    }

    return stateValue;
};

/**
 * Converts a sceneXML string into the expected state for the redux store.
 * All IDs from the sceneXML will have "_" appended to the front such that
 * it does not conflict with newly created IDs.
 * @param {string} xml - The sceneXML to load. The source of the XML doesn't matter.
 * @returns {StateObject} - The composed state to be ingested.
 */
export default function fromXml(
    xml: string,
    finishList: FinishData[],
    cylinderWarp?: Record<string, any>,
    markers?: Record<string, any>,
    garmentPlacements?: Record<string, any>,
) {
    const objectTree = new X2JS().xml_str2json(xml);

    return composeComposite(objectTree.Dip, finishList, cylinderWarp, markers, garmentPlacements);
}
